import { deleteLogo, getLogo, uploadLogo } from '@/services/api';
import { getUserSettings, setUserSettings } from '@/services/api/studioApi';
import { logger } from '@/services/logger';
import { ILogoRequest, IUserSettingsData } from '@d-id/types';
import { useMutation, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import { defaultIUserSettings } from './constants';

export const USER_SETTINGS_KEY = 'user-settings';
export const LOGO_KEY = 'logo';

const MaxRetries = 3;

export function useUserSettingsFromDB({ enabled }: { enabled: boolean }): UseQueryResult<IUserSettingsData> {
    return useQuery<IUserSettingsData>(
        USER_SETTINGS_KEY,
        async () => {
            try {
                const data = await getUserSettings();
                return data || defaultIUserSettings;
            } catch (error) {
                logger.error('Could not fetch user settings', { error });
                return defaultIUserSettings;
            }
        },
        {
            refetchOnWindowFocus: false,
            retry: MaxRetries,
            retryDelay: 500,
            enabled,
            // TODO: delete this select after refactor migration ends
            select(data) {
                const isOnBoarding = data.onBoarding ? Object.keys(data.onBoarding).length > 0 : false;
                const isEditor = data.editor ? Object.keys(data.editor).length > 0 : false;
                const isMigrations = data.migrations ? Object.keys(data.migrations).length > 0 : false;
                if (isOnBoarding || isEditor || isMigrations) {
                    const newData = {
                        features: {
                            ...data.features,
                            onBoarding: isOnBoarding ? data.onBoarding : data.features?.onBoarding,
                            editor: isEditor ? data.editor : data.features?.editor,
                        },
                        messages: {
                            ...data.messages,
                            creditMigrationSeen: isMigrations ? data.migrations?.creditMigrationSeen : data.messages?.creditMigrationSeen,
                        },
                        preferences: {
                            ...data.preferences,
                        },
                        experiments: {
                            ...data.experiments,
                        },
                    };
                    setUserSettings(newData, true);
                    return newData;
                }
                return data;
            },
        }
    );
}

export function useUploadLogo() {
    const queryClient = useQueryClient();

    return useMutation(uploadLogo, {
        onSuccess: async () => {
            await queryClient.invalidateQueries([LOGO_KEY]);
        },
    });
}

export function useDeleteLogo() {
    const queryClient = useQueryClient();

    return useMutation(deleteLogo, {
        onSuccess: async () => {
            await queryClient.invalidateQueries([LOGO_KEY]);
        },
    });
}

export function useGetLogo(): UseQueryResult<ILogoRequest> {
    return useQuery<ILogoRequest>(LOGO_KEY, getLogo, {
        retry: false,
    });
}
